import { useEffect, useRef, useState } from "react";
import { ProgressSteps } from "../../components/ProgressSteps/ProgressSteps";
import { StepDate } from "./steps/StepDate";
import { StepForm } from "./steps/StepForm";
import { StepConfirmation } from "./steps/StepConfirmation";
import { useBookingState } from "./useBookingState";
import { notifyRequestSubmitted, notifyStepChange, observeHeight } from "../../utils/webflowBridge";

export function BookingFlow() {
  const {
    step,
    selectedDate,
    setSelectedDate,
    formValues,
    setFormValues,
    idempotencyKey,
    confirmation,
    goToStep2,
    goBackToStep1,
    completeWithConfirmation,
  } = useBookingState();

  const [dateUnavailableMessage, setDateUnavailableMessage] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rootRef.current) return;
    return observeHeight(rootRef.current);
  }, []);

  useEffect(() => {
    notifyStepChange(step);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  function handleDateUnavailable(message: string) {
    setDateUnavailableMessage(message);
    goBackToStep1();
  }

  function handleSelectDate(date: string) {
    setDateUnavailableMessage(null);
    setSelectedDate(date);
  }

  function handleSubmitted(result: Parameters<typeof completeWithConfirmation>[0]) {
    completeWithConfirmation(result);
    notifyRequestSubmitted();
  }

  return (
    <div ref={rootRef} className="dmm-app-root">
      <h1 style={{ fontSize: "1.8rem", marginBottom: "8px" }}>Reserveer jouw moment</h1>
      <p style={{ marginBottom: "32px", maxWidth: "65ch", color: "var(--dmm-color-text)" }}>
        Kies eerst de datum van jouw evenement. Daarna vragen we je om een paar gegevens, zodat we
        de beschikbaarheid kunnen controleren en een passend voorstel kunnen sturen.
      </p>

      <ProgressSteps currentStep={step} />

      {dateUnavailableMessage && step === 1 && (
        <p
          role="alert"
          style={{ color: "var(--dmm-color-danger)", fontWeight: 600, marginBottom: "16px" }}
        >
          {dateUnavailableMessage}
        </p>
      )}

      {step === 1 && (
        <StepDate
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
          onContinue={goToStep2}
        />
      )}

      {step === 2 && selectedDate && (
        <StepForm
          eventDate={selectedDate}
          formValues={formValues}
          onChange={setFormValues}
          idempotencyKey={idempotencyKey}
          onBack={goBackToStep1}
          onSubmitted={handleSubmitted}
          onDateUnavailable={handleDateUnavailable}
        />
      )}

      {step === 3 && confirmation && <StepConfirmation confirmation={confirmation} />}
    </div>
  );
}
